import { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Label,
  FormGroup,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { toast } from "react-toastify";
import { dashboard } from "../../helpers/backend_helper";
import { CommonDatePicker } from "../../components/Common/Datepicker/CommonDatePicker";
import { getDefaultDates, getEndOfDay, getEndOfDayTimestamp, getStartOfDayTimestamp, getTimestamp, timeStamptoDate } from "../../helpers/formatDate";
import { fetchBeats, fetchDivisions, fetchRanges, fetchRanges2, fetchRounds, fetchRounds2 } from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { CommonMyDatePicker } from "../../components/Common/Datepicker/CommonMyDatePicker";
import Datatable from "../../components/Common/Datatable/Datatable";
import { exportReportsToExcel } from "../../helpers/general";

export default function Reports() {
  const defaultDates = getDefaultDates();
  const dispatch = useDispatch();
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [startDate, setStartDate] = useState(defaultDates.start.getTime());
  const [endDate, setEndDate] = useState(defaultDates.end.getTime());
  const [searchStatus, setSearchStatus] = useState("");
  const [searchIncidentType, setSearchIncidentType] = useState("");
  const [loading, setLoading] = useState(false);
  const [uniqueStatuses, setUniqueStatuses] = useState([
    "open",
    "assigned",
    "acknowledged",
    "working",
    "resolved",
    "closed",
    "reopen",
  ]);
  const [formData, setFormData] = useState({
    division: "",
    range: "",
    round: "",
    beat: "",
    assignedUser: "",
    status: "",
  });
  const [search, setSearch] = useState("");
  const [ticketId, setTicketId] = useState("");

  const [uniqueIncidentTypes, setUniqueIncidentTypes] = useState([]);
  const { divisions, ranges, rounds } = useSelector(
    (state) => state.Common || {}
  );

  const fetchReports = async (filterParams = {}) => {
    setLoading(true);
    const payload = {
      act: "dashboard",
      subact: "reports",
      info: {},
      filter: {
        fromDate: getStartOfDayTimestamp(startDate),
        toDate: getEndOfDayTimestamp(endDate),
        ...filterParams, // Include additional filter parameters
      },
    };

    try {
      const response = await dashboard(payload);
      if (response?.message) {
        const data = response?.data || [];
        setReports(data);
        setFilteredReports(data);

        const incidents = JSON.parse(
          localStorage.getItem("incidentTypes") || "[]"
        );
        setUniqueIncidentTypes(incidents);
      }
    } catch (error) {
      toast.error("Error in fetching reports", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };


  const fetchReports2 = async (filterParams = {}) => {
    setLoading(true);
    const payload = {
      act: "dashboard",
      subact: "reports",
      info: {},
      filter: {
        fromDate: getStartOfDayTimestamp(startDate),
        toDate: getEndOfDayTimestamp(endDate),
        ...filterParams, // Include additional filter parameters
      },
    };

    try {
      const response = await dashboard(payload);
      if (response?.message) {
        const data = response?.data || [];
        return data
      }
    } catch (error) {
      toast.error("Error in fetching reports", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchReports();
  }, []);

  const applyFilters = async () => {
    const apiFilters = {
      fromDate: getStartOfDayTimestamp(startDate),
      toDate: getEndOfDayTimestamp(endDate),
    };

    // Add other filters to API payload
    if (searchStatus) {
      apiFilters.status = searchStatus;
    }
    if (search) {
      apiFilters.assignedUser = search;
    }

    if (searchIncidentType) {
      const selectedIncident = uniqueIncidentTypes.find(
        (incident) => incident.id === searchIncidentType
      );
      if (selectedIncident) {
        apiFilters.incidentTypeId = selectedIncident.incidentTypeId;
      }
    }

    if (formData.division && formData.division !== "0") {
      apiFilters.divisionId = formData.division;
    }

    if (formData.range && formData.range !== "0") {
      apiFilters.rangeId = formData.range;
    }

    if (formData.round && formData.round !== "0") {
      apiFilters.roundId = formData.round;
    }

    if (formData.beat && formData.beat !== "0") {
      apiFilters.beatId = formData.beat;
    }

    const filterreports = await fetchReports2(apiFilters);
    let filtered = [...filterreports];

    // Apply other client-side filters only if API doesn't handle them
    if (searchStatus) {
      filtered = filtered.filter(
        (report) => report.status.toLowerCase() === searchStatus.toLowerCase()
      );
    }
    if (ticketId) {
      filtered = filtered.filter(
        (report) => report?.serialId?.toLowerCase().includes(ticketId.toLowerCase())
      );
    }

    if (searchIncidentType) {
      const selectedIncident = uniqueIncidentTypes.find(
        (incident) => incident.id === searchIncidentType
      );
      if (selectedIncident) {
        filtered = filtered.filter(
          (report) => report.incidentTypeId == selectedIncident.incidentTypeId
        );
      }
    }

    if (formData.division && formData.division !== "0") {
      filtered = filtered.filter(
        (report) => report.divisionId == formData.division
      );
    }

    if (formData.range && formData.range !== "0") {
      filtered = filtered.filter(
        (report) => report.rangeId == formData.range
      );
    }

    if (formData.round && formData.round !== "0") {
      filtered = filtered.filter(
        (report) => report.roundId == formData.round
      );
    }

    if (formData.beat && formData.beat !== "0") {
      filtered = filtered.filter(
        (report) => report.beatId == formData.beat
      );
    }

    setFilteredReports(filtered);
  };

  const resetFilters = () => {
    setStartDate(defaultDates.start.getTime());
    setEndDate(defaultDates.end.getTime());
    setSearchStatus("");
    setSearchIncidentType("");
    setFormData({
      division: "",
      range: "",
      round: "",
      beat: "",
      assignedUser: "",
      status: "",
    });
    setSearch("");
    setTicketId("")
    setFilteredReports(reports);
  };

  const columns = [
    {
      name: "Ticket ID",
      selector: (row) => row.serialId || "-",
      sortable: true,
    },
    {
      name: "Compartment",
      selector: (row) => row.compId || "-",
      sortable: true,
    },
    {
      name: "Division",
      selector: (row) => row.divisionName || "-",
      sortable: true,
    },
    {
      name: "Range",
      selector: (row) => row.rangeName || "-",
      sortable: true,
    },
    {
      name: "Beat",
      selector: (row) => row.beatName || "-",
      sortable: true,
    },
    {
      name: "Round",
      selector: (row) => row.roundName || "-",
      sortable: true,
    },

    {
      name: "Description",
      selector: (row) => row.description || "-",
      wrap: true,
    },
    {
      name: "Status",
      selector: (row) => row.status || "-",
      sortable: true,
      cell: (row) => (
        <span className={`badge ${getBadgeColor(row.status)}`}>
          {row.status}
        </span>
      ),
    },
    {
      name: "Incident Type",
      selector: (row) => row.incidentTypeId || "-",
      cell: (row) => {
        const incident = uniqueIncidentTypes.find(
          (incident) => incident.incidentTypeId === row.incidentTypeId
        );
        return incident?.incidentName || "-";
      },

      sortable: true,
    },
  ];

  const getBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case "assigned":
        return "bg-warning";
      case "resolved":
        return "bg-success";
      case "pending":
        return "bg-danger";
      default:
        return "bg-info";
    }
  };

  useEffect(() => {
    dispatch(fetchDivisions());
    // Only try to load initial data if we have form values on first render
    const { division, range, round, beat } = formData;
    if (division) {
      dispatch(fetchRanges(division));

      if (range) {
        dispatch(fetchRounds(range));

        if (round) {
          dispatch(fetchBeats(round));
        }
      }
    }
  }, []);

  // Modify handleInputChange to remove map data updates
  const handleInputChange = (e, key, fetchAction) => {
    const value = e.target.value;

    // Reset dependent dropdowns
    let updatedFormData = { ...formData, [key]: value };

    if (key === "division") {
      updatedFormData = {
        ...updatedFormData,
        range: "",
        round: "",
        beat: "",
      };

      // If All is selected (value is '' or '0'), don't fetch ranges
      if (value && value !== "0") {
        // Fetch ranges for this division
        if (fetchAction) {
          dispatch(fetchAction(Number(value)));
        }
      }
    } else if (key === "range") {
      updatedFormData = {
        ...updatedFormData,
        round: "",
        beat: "",
      };

      // If All is selected (value is '' or '0'), don't fetch rounds
      if (value && value !== "0") {
        // Fetch rounds for this range
        if (fetchAction) {
          dispatch(fetchAction(Number(value)));
        }
      }
    } else if (key === "round") {
      updatedFormData = {
        ...updatedFormData,
        beat: "",
      };
    }

    // Update form data
    setFormData(updatedFormData);
  };


  const dependentDropdowns = [
    {
      label: "Division",
      key: "division",
      data: divisions,
      fetch: fetchRanges2,
    },
    {
      label: "Range",
      key: "range",
      data: ranges,
      fetch: fetchRounds2,
    },
    {
      label: "Round",
      key: "round",
      data: rounds,
    },
  ];
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };
  const handleTicketFilter = (e) => {
    setTicketId(e.target.value);
  };
  return (
    <div>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Control Room" breadcrumbItem="Reports" />

          <Card>
            <CardBody>
              <h4 className="card-title mb-4">Report Filters</h4>
              <Row>
                <Col md={2}>
                  <FormGroup>
                    <div>
                      <CommonDatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={(timestamp) => setStartDate(timestamp)}
                        name={"Start Date"}
                        preserveTime={true}
                      />
                    </div>
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <div>
                      <CommonMyDatePicker
                        label="End Date"
                        value={endDate}
                        onChange={(timestamp) => setEndDate(getTimestamp(getEndOfDay(timestamp)))}
                        name={"End Date"}
                        preserveTime={true}
                      />
                    </div>
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label>Status</Label>
                    <Input
                      type="select"
                      value={searchStatus}
                      onChange={(e) => setSearchStatus(e.target.value)}
                    >
                      <option value="">All Statuses</option>
                      {uniqueStatuses.map((status, index) => (
                        <option key={index + status} value={status}>
                          {status.charAt(0).toUpperCase() +
                            status.slice(1).toLowerCase()}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Incident Type</Label>
                    <Input
                      type="select"
                      value={searchIncidentType}
                      onChange={(e) => setSearchIncidentType(e.target.value)}
                    >
                      <option value="">All Types</option>
                      {uniqueIncidentTypes.map((incident) => (
                        <option key={incident.id} value={incident.id}>
                          {incident.incidentName}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <Label for="search" className="fw-medium">
                    Search
                  </Label>
                  <InputGroup>
                    <Input
                      id="search"
                      type="text"
                      placeholder="Search by assigned to user..."
                      value={search}
                      onChange={handleSearchChange}
                      className="form-control-modern"
                    />
                    <InputGroupText className="bg-light">
                      <i className="ri-search-line text-muted" />
                    </InputGroupText>
                  </InputGroup>
                </Col>

              </Row>
              <Row className="mb-3">
                {dependentDropdowns?.map(({ label, key, data, fetch }, index) => (
                  <Col md={2} key={key}>
                    <FormGroup>
                      <Label for={key} className="fw-medium">
                        {label}
                      </Label>
                      <Input
                        type="select"
                        id={key}
                        value={formData[key] || ""}
                        onChange={(e) => handleInputChange(e, key, fetch)}
                        disabled={
                          (index > 0 &&
                            !formData[
                            ["division", "range", "round"][index - 1]
                            ]) ||
                          (index > 0 && formData["division"] === "0")
                        }
                        className="form-control-modern"
                      >
                        <option value="0">All {label}s</option>
                        {Array.isArray(data?.data) &&
                          data.data.map((item) => (
                            <option
                              key={item[`${key}Id`]}
                              value={item[`${key}Id`]}
                            >
                              {item.name ||
                                item.divisionName ||
                                item.rangeName ||
                                item.beatName ||
                                item.roundName}
                            </option>
                          ))}
                      </Input>
                    </FormGroup>
                  </Col>
                ))}


                <Col md={3}>
                  <Label for="search" className="fw-medium">
                    Ticket Search
                  </Label>
                  <InputGroup>
                    <Input
                      id="search"
                      type="text"
                      placeholder="Search by Ticket ID"
                      value={ticketId}
                      onChange={handleTicketFilter}
                      className="form-control-modern"
                    />
                    <InputGroupText className="bg-light">
                      <i className="ri-search-line text-muted" />
                    </InputGroupText>
                  </InputGroup>
                </Col>

                <Col md={3} className="d-flex mb-3 gap-2 justify-content-end align-items-end">
                  <Button color="primary" onClick={applyFilters}>
                    Apply
                  </Button>
                  <Button color="secondary" onClick={resetFilters}>
                    Reset
                  </Button>
                </Col></Row>

            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="d-flex justify-content-between mb-3">
                <h4 className="card-title">Reports List</h4>
                <Button color="success" onClick={() => exportReportsToExcel(filteredReports)}>
                  <i className="mdi mdi-file-excel me-1"></i> Export to Excel
                </Button>
              </div>

              <Datatable
                columns={columns}
                key={filteredReports}
                data={filteredReports}
                highlightOnHover
                noDataComponent={
                  <div className="text-center my-3">No reports found</div>
                }
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </div>
  );
}
